import React from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import {pluralize} from "@rw/string_utils";

import {IStore} from "../../app/reducers/hybrid_reducer";
import {getOfferListUrl} from "../../offer/list/url_utils/get_offer_list_url";
import {UMakeKeys} from "../../offer/list/url_utils/tag_utils";
import {getMakeSlug} from "../../offer/utils/get_make_slug";
import {Container} from "./CarBrandsSection";

const MAX_MAKES = 40;
const pluralOffer = pluralize(["oferta", "oferty", "ofert"]);

export const HomepageSeoIndex: React.FC = () => {
    const makes = useSelector((store: IStore) => store.carList.cars.allMakes);

    const makeLinks = (makes || [])
        .map((make) => ({...make, slug: getMakeSlug(make.make)}))
        .filter((make): make is typeof make & {slug: UMakeKeys} => Boolean(make.slug) && make.count > 0)
        .sort((a, b) => b.count - a.count || a.make.localeCompare(b.make, "pl"))
        .slice(0, MAX_MAKES);

    if (makeLinks.length === 0) {
        return null;
    }

    return (
        <section css={sectionStyle} aria-labelledby="seo-makes-title">
            <Container>
                <div css={headingStyle}>
                    <h2 id="seo-makes-title">Samochody według marki</h2>
                    <p>Przeglądaj marki z największą liczbą aktualnych ofert.</p>
                </div>

                <nav aria-label="Marki samochodów">
                    <ul css={linkGrid}>
                        {makeLinks.map((make) => (
                            <li key={make.slug}>
                                <a href={getOfferListUrl({offerType: "osobowe", type: make.slug})}>
                                    <span>{make.make}</span>
                                    <small>
                                        {make.count} {pluralOffer(make.count)}
                                    </small>
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            </Container>
        </section>
    );
};

const sectionStyle = (theme: Theme) => css`
    padding: 40px 20px 56px;
    background: ${theme.colors.brand_white};
    border-top: 1px solid ${theme.colors.gray_very_bright};
`;

const headingStyle = (theme: Theme) => css`
    max-width: 720px;
    margin-bottom: 20px;

    h2 {
        margin: 0 0 8px;
        color: ${theme.colors.brand_black};
        font-size: 2.4rem;
        line-height: 1.25;
    }

    p {
        margin: 0;
        color: ${theme.colors.gray_darker};
        font-size: 1.4rem;
    }
`;

const linkGrid = (theme: Theme) => css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px 18px;
    margin: 0;
    padding: 0;
    list-style: none;

    @media (min-width: ${theme.breakpoints.screen_xs}) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (min-width: ${theme.breakpoints.screen_md}) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (min-width: ${theme.breakpoints.screen_lg}) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    a {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
        padding: 6px 0;
        color: ${theme.colors.gray_dark};
        font-size: 1.3rem;
        font-weight: 600;
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    a:hover {
        color: ${theme.colors.brand_primary};
    }

    small {
        color: ${theme.colors.gray_warm};
        font-size: 1.1rem;
        font-weight: 500;
        text-decoration: none;
        white-space: nowrap;
    }
`;
